<template>
  <div>
    <!-- isGlobal / isTemplate flags (admin only) -->
    <div v-if="isAdmin" style="max-width: 600px; margin: 0 auto 16px;">
      <v-checkbox
        v-model="isGlobalStructure"
        label="Global Structure (available to all users)"
        hide-details
      />
      <v-checkbox
        v-model="isTemplateStructure"
        label="Template Structure (only visible in settings, not when creating tournaments)"
        hide-details
      />
    </div>

    <!-- Announcement (live tournament only) -->
    <div v-if="showAnnouncement" style="max-width: 600px; margin: 0 auto 16px;">
      <v-text-field
        v-model="announcementText"
        label="Announcement"
        density="compact"
        variant="outlined"
        maxlength="80"
        counter
        hide-details="auto"
      />
    </div>

    <!-- Tab navigation -->
    <div class="mainDisplayButtons" style="margin-bottom: 10px; display: flex; align-items: center; justify-content: center;">
      <v-btn small @click="mainDisplay='BASIC'"   class="mx-1" color="primary" variant="outlined" :active="mainDisplay === 'BASIC'">Buy Ins</v-btn>
      <v-btn small @click="mainDisplay='LEVELS'"  class="mx-1" color="primary" variant="outlined" :active="mainDisplay === 'LEVELS'">Levels</v-btn>
      <v-btn small @click="mainDisplay='PAYOUTS'" class="mx-1" color="primary" variant="outlined" :active="mainDisplay === 'PAYOUTS'">Payouts</v-btn>
      <v-btn small @click="mainDisplay='CHIPS'"   class="mx-1" color="primary" variant="outlined" :active="mainDisplay === 'CHIPS'">Chips</v-btn>
      <v-btn small @click="mainDisplay='PLAYERS'" class="mx-1" color="primary" variant="outlined" :active="mainDisplay === 'PLAYERS'">Players</v-btn>
      <v-btn small @click="mainDisplay='DISPLAY'"  class="mx-1" color="primary" variant="outlined" :active="mainDisplay === 'DISPLAY'">Display</v-btn>
    </div>

    <!-- Buy Ins -->
    <div v-if="mainDisplay === 'BASIC'">
      <h2>Buyins</h2>
      <v-form>
        <div style="display: flex; align-items: center; gap: 16px; max-width: 600px; margin-bottom: 8px;">
          <v-text-field
            v-model.number="buyinAmount"
            label="Buy-in Amount ($)"
            type="number"
            min="0"
            prefix="$"
            style="flex: 0 0 200px; max-width: 200px;"
          />
          <v-text-field
            v-model.number="buyinRake"
            label="Rake ($)"
            type="number"
            min="0"
            prefix="$"
            hint="Deducted from jackpot"
            persistent-hint
            style="flex: 0 0 150px; max-width: 150px;"
          />
        </div>
        <div style="display: flex; align-items: center; max-width: 700px; margin-bottom: 8px; gap: 16px;">
          <v-text-field
            v-model.number="rebuyAmount"
            label="Rebuy Amount ($)"
            type="number"
            min="0"
            prefix="$"
            :disabled="!rebuyAllowed"
            style="flex: 0 0 200px; max-width: 200px;"
          />
          <v-text-field
            v-if="rebuyAllowed"
            v-model.number="rebuyRake"
            label="Rake ($)"
            type="number"
            min="0"
            prefix="$"
            hint="Deducted from jackpot"
            persistent-hint
            style="flex: 0 0 150px; max-width: 150px;"
          />
          <v-checkbox
            v-model="rebuyAllowed"
            label="Yes/No"
            hide-details
            style="margin: 0; min-width: 100px;"
          />
          <v-text-field
            v-if="rebuyAllowed"
            v-model.number="rebuyEndLevel"
            label="Rebuys Allowed Through"
            type="number"
            min="0"
            hint="Enter the level number (e.g., 3 means rebuys allowed through end of level 3). 0 to allow rebuys for all levels."
            persistent-hint
            style="flex: 1 1 auto; max-width: 250px;"
          />
        </div>
        <div style="display: flex; align-items: center; max-width: 500px; margin-bottom: 8px; gap: 16px;">
          <v-text-field
            v-model.number="addonAmount"
            label="Add-on Amount ($)"
            type="number"
            min="0"
            prefix="$"
            :disabled="!addonAllowed"
            style="flex: 0 0 200px; max-width: 200px;"
          />
          <v-text-field
            v-if="addonAllowed"
            v-model.number="addonRake"
            label="Rake ($)"
            type="number"
            min="0"
            prefix="$"
            hint="Deducted from jackpot"
            persistent-hint
            style="flex: 0 0 150px; max-width: 150px;"
          />
          <v-checkbox
            v-model="addonAllowed"
            label="Yes/No"
            hide-details
            style="margin: 0; min-width: 100px;"
          />
        </div>
      </v-form>
    </div>

    <!-- Chips -->
    <div v-if="mainDisplay === 'CHIPS'">
      <h2>Chips</h2>
      <v-form>
        <h3 class="mt-6">Starting Stacks</h3>
        <v-text-field
          v-model.number="buyinChips"
          label="Buy-in Chips"
          type="number"
          min="0"
          required
          style="max-width: 300px;"
        />
        <v-text-field
          v-model.number="addonChips"
          label="Add-on Chips"
          type="number"
          min="0"
          :disabled="!addonAllowed"
          style="max-width: 300px;"
        />
        <h3 class="mt-6 mb-2">Chip Denominations</h3>

        <!-- Clickable chip row -->
        <div style="display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 16px; padding: 16px; background-color: #f0f0f0; border-radius: 8px;">
          <div
            v-for="(chip, index) in chipDenominations"
            :key="index"
            class="chip-visual"
            :style="{
              backgroundColor: chip.color,
              borderColor: chip.accentColor,
              cursor: 'pointer',
              outline: selectedChipIndex === index ? '3px solid #1976d2' : '3px solid transparent',
              outlineOffset: '3px'
            }"
            @click="selectedChipIndex = index"
          >
            <div class="chip-value" :style="{ color: getContrastingTextColor(chip.color) }">{{ chip.value }}</div>
          </div>
        </div>

        <!-- Editor panel for selected chip -->
        <div v-if="selectedChipIndex !== null && chipDenominations[selectedChipIndex]" style="max-width: 560px; padding: 16px; border: 1px solid #e0e0e0; border-radius: 8px; margin-bottom: 12px;">
          <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
            <v-text-field
              v-model.number="chipDenominations[selectedChipIndex].value"
              label="Value"
              type="number"
              min="1"
              density="compact"
              variant="outlined"
              hide-details
              style="max-width: 120px;"
            />
            <div style="flex: 1;"></div>
            <v-btn color="error" variant="outlined" size="small" @click="removeSelectedChip">
              <v-icon start>mdi-delete</v-icon>Delete
            </v-btn>
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <div>
              <div style="font-size: 0.85rem; font-weight: 500; margin-bottom: 6px; color: rgba(0,0,0,0.7);">Chip Color</div>
              <ChipColorPicker v-model="chipDenominations[selectedChipIndex].color" />
            </div>
            <div>
              <div style="font-size: 0.85rem; font-weight: 500; margin-bottom: 6px; color: rgba(0,0,0,0.7);">Accent Color</div>
              <ChipColorPicker v-model="chipDenominations[selectedChipIndex].accentColor" />
            </div>
          </div>
        </div>
        <div v-else style="color: #888; font-size: 0.875rem; margin-bottom: 12px;">Click a chip above to edit its colors.</div>

        <v-btn color="primary" @click="addChip">Add Chip</v-btn>
      </v-form>
    </div>

    <!-- Players -->
    <div v-if="mainDisplay === 'PLAYERS'">
      <h2>Table Configuration</h2>
      <v-form>
        <v-checkbox v-model="trackPlayersByName" label="Track players by name" />
        <v-text-field
          v-model.number="maxTables"
          label="Max Tables"
          type="number"
          min="0"
          style="max-width: 300px;"
        />
        <v-text-field
          v-model.number="maxPlayersPerTable"
          label="Max Players Per Table"
          type="number"
          min="2"
          style="max-width: 300px;"
        />
      </v-form>
    </div>

    <!-- Display -->
    <div v-if="mainDisplay === 'DISPLAY'">
      <h2>Timer Display</h2>
      <v-form style="max-width: 700px;">
        <v-text-field
          v-model="displayConfig.displayName"
          label="Display Name (shown at top of timer screen)"
          hint="Leave blank to hide"
          persistent-hint
          maxlength="40"
          counter
          style="max-width: 400px;"
        />

        <h3 class="mt-6 mb-2">Accent Color</h3>
        <div style="display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 24px;">
          <div
            v-for="swatch in ACCENT_COLORS"
            :key="swatch.name"
            :title="swatch.name"
            class="accent-swatch"
            :style="{
              backgroundColor: swatch.value || '#e0e0e0',
              outline: displayConfig.accentColor === swatch.value ? '3px solid #1976d2' : '3px solid transparent',
            }"
            @click="displayConfig.accentColor = swatch.value"
          >
            <v-icon v-if="swatch.value === null" size="small">mdi-cancel</v-icon>
          </div>
        </div>

        <h3 class="mb-2">Modules</h3>
        <v-table style="max-width: 500px;">
          <thead>
            <tr>
              <th>Module</th>
              <th>Show</th>
              <th>Size</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="mod in DISPLAY_MODULES" :key="mod.key">
              <td>{{ mod.label }}</td>
              <td>
                <v-checkbox
                  v-if="mod.hideable"
                  v-model="displayConfig.modules[mod.key].show"
                  hide-details
                  density="compact"
                />
                <span v-else style="color: #999;">Always on</span>
              </td>
              <td>
                <v-select
                  v-model="displayConfig.modules[mod.key].size"
                  :items="DISPLAY_SIZES"
                  density="compact"
                  variant="outlined"
                  hide-details
                  style="max-width: 110px;"
                />
              </td>
            </tr>
          </tbody>
        </v-table>

        <v-checkbox
          v-model="displayConfig.showLastRebuyNotice"
          label="Show last-rebuy notice (derived from Rebuys Allowed Through End of Level, above)"
          class="mt-4"
          hide-details
        />
      </v-form>
    </div>

    <!-- Max Players Confirmation Dialog -->
    <v-dialog v-model="showMaxPlayersDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">Confirm Player Count</v-card-title>
        <v-card-text>
          Are you sure you want more than 10 players per table? This is unusual for most tournaments.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelMaxPlayersChange">Cancel</v-btn>
          <v-btn color="primary" text @click="confirmMaxPlayersChange">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Payouts -->
    <div v-if="mainDisplay === 'PAYOUTS'">
      <h2>Payout Tables</h2>
      <div style="margin-bottom: 16px;">
        <v-btn color="primary" @click="addPayoutTable">Add Payout Table</v-btn>
      </div>
      <div v-for="(table, tableIndex) in payoutTables" :key="tableIndex" style="margin-bottom: 32px; padding: 16px; border: 1px solid #ddd; border-radius: 8px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
          <h3>Payout Table {{ tableIndex + 1 }}</h3>
          <v-btn icon color="error" @click="removePayoutTable(tableIndex)" :disabled="payoutTables.length <= 1">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
          <v-text-field v-model.number="table.minEntries" label="Min Entries" type="number" min="1" style="max-width: 150px;" @input="validateEntryRanges" />
          <span>to</span>
          <v-text-field v-model.number="table.maxEntries" label="Max Entries" type="number" min="1" style="max-width: 150px;" @input="validateEntryRanges" />
          <span>players</span>
        </div>
        <div style="margin-bottom: 16px;">
          <v-text-field v-model.number="table.numberOfPayouts" label="Number of Places to Pay" type="number" min="1" max="20" style="max-width: 300px;" @input="updatePrizesTemplate(tableIndex)" />
        </div>
        <div style="margin-bottom: 16px;">
          <v-select v-model="table.rounding" :items="roundingOptions" item-title="label" item-value="value" label="Round Prizes to Nearest" style="max-width: 300px;" />
        </div>
        <div style="margin-bottom: 16px;">
          <v-checkbox v-model="table.usePercent" label="Use Percent (%) for Payouts" hide-details />
        </div>
        <v-table style="max-width: 400px;">
          <thead>
            <tr>
              <th>Place</th>
              <th>{{ table.usePercent ? 'Percent (%)' : 'Amount ($)' }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(_, idx) in table.prizesTemplate" :key="idx">
              <td>{{ idx + 1 }}</td>
              <td>
                <v-text-field
                  v-model.number="table.prizesTemplate[idx]"
                  type="number"
                  :min="0"
                  :max="table.usePercent ? 100 : undefined"
                  :prefix="table.usePercent ? '' : '$'"
                  style="max-width: 120px;"
                  @input="onPrizePercentChange(tableIndex, idx)"
                />
              </td>
              <td>
                <v-btn icon color="error" @click="removePrize(tableIndex, idx)" :disabled="table.prizesTemplate.length <= 1">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
        <div style="margin-top: 8px;">
          <v-btn color="primary" small @click="addPrize(tableIndex)">Add Place</v-btn>
        </div>
        <div v-if="table.usePercent && getPrizesTotal(tableIndex) !== 100" style="color: red; margin-top: 8px;">
          Total must be 100%. Current: {{ getPrizesTotal(tableIndex) }}%
        </div>
        <div v-if="!table.usePercent && getDollarPayoutWarning(tableIndex)" style="color: orange; margin-top: 8px;">
          ⚠️ {{ getDollarPayoutWarning(tableIndex) }}
        </div>
        <div v-if="hasEntryRangeConflict(tableIndex)" style="color: orange; margin-top: 8px;">
          ⚠️ Entry range overlaps with another table
        </div>
      </div>
    </div>

    <!-- Levels -->
    <div v-if="mainDisplay === 'LEVELS'">
      <div style="display: flex; align-items: flex-start; gap: 16px; margin-top: 32px; margin-bottom: 16px; max-width: 848px; margin-left: auto; margin-right: auto;">
        <v-text-field
          v-model.number="durationMinutes"
          label="Default Level Length (minutes)"
          type="number"
          min="1"
          @blur="onDurationBlur('level')"
          required
          style="flex: 0 0 200px; max-width: 200px;"
        />
        <v-text-field
          v-model.number="breakMinutes"
          label="Default Break Length (minutes)"
          type="number"
          min="1"
          @blur="onDurationBlur('break')"
          required
          style="flex: 0 0 200px; max-width: 200px;"
        />
        <v-text-field
          v-model.number="startCountdownSeconds"
          label="Start Countdown (seconds)"
          type="number"
          min="0"
          hint="Countdown before Level 1 begins. 0 = disabled."
          persistent-hint
          style="flex: 0 0 200px; max-width: 200px;"
        />
        <v-text-field
          v-model.number="betweenRoundsPauseSeconds"
          label="Pause Between Rounds (seconds)"
          type="number"
          min="0"
          hint="Freeze new level time briefly after each round ends. 0 = disabled."
          persistent-hint
          style="flex: 0 0 200px; max-width: 200px;"
        />
      </div>
      <h2 class="mb-2">Blind Structure</h2>
      <div style="overflow-x: auto; -webkit-overflow-scrolling: touch;">
      <v-table class="mt-4" style="max-width: 1000px;">
        <thead>
          <tr>
            <th>Level</th>
            <th>Small Blind</th>
            <th>Big Blind</th>
            <th>Ante</th>
            <th>Duration (min)</th>
            <th>Use BB Ante</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, idx) in levels" :key="idx">
            <tr>
              <td>{{ row.isBreak ? 'Break' : getNormalLevelNumber(idx) }}</td>
              <td>
                <v-text-field :ref="(el) => setSmallBlindRef(el, idx)" v-model.number="row.smallBlind" type="number" min="0" style="min-width: 130px;" @blur="onSmallBlindChange(idx)" :disabled="row.isBreak" />
              </td>
              <td>
                <v-text-field v-model.number="row.bigBlind" type="number" min="0" style="min-width: 130px;" :disabled="row.isBreak" @blur="onBigBlindBlur(idx)" />
              </td>
              <td>
                <v-text-field v-model.number="row.bigBlindAnte" type="number" min="0" style="min-width: 130px;" :disabled="row.useBBAnte || row.isBreak" />
              </td>
              <td>
                <v-text-field v-model.number="row.duration" type="number" min="1" style="min-width: 130px;" :placeholder="row.isBreak ? String(breakMinutes) : String(durationMinutes)" />
              </td>
              <td>
                <v-checkbox v-model="row.useBBAnte" @change="onBBAnteChange(idx)" hide-details :disabled="row.isBreak" />
              </td>
              <td>
                <v-btn icon color="primary" @click="showAddLevelDialog(idx)"><v-icon>mdi-plus</v-icon></v-btn>
              </td>
              <td>
                <v-btn icon color="error" @click="deleteRow(idx)" :disabled="levels.length === 1"><v-icon>mdi-delete</v-icon></v-btn>
              </td>
            </tr>
            <tr v-if="row.isBreak">
              <td colspan="8" style="padding-top: 0;">
                <v-text-field
                  v-model="row.breakMessage"
                  label="Break Message (shown on display instead of 'Players are on break')"
                  placeholder="Players are on break"
                  density="compact"
                  maxlength="80"
                  counter
                  hide-details="auto"
                  style="max-width: 500px;"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
      </div>

      <!-- Add Level Dialog -->
      <v-dialog v-model="addLevelDialog" max-width="400" @after-enter="focusAddLevelBtn">
        <v-card>
          <v-card-title>Add Level</v-card-title>
          <v-card-text>
            <div style="margin-bottom: 16px;">What type of level would you like to add?</div>
            <v-radio-group v-model="newLevelType">
              <v-radio value="normal" label="Normal Level"></v-radio>
              <v-radio value="break" label="Break Level"></v-radio>
            </v-radio-group>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="addLevelDialog = false">Cancel</v-btn>
            <v-btn ref="addLevelBtnRef" color="primary" text @click="confirmAddLevel">Add</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

    <!-- Duration Update Confirmation Dialog -->
    <v-dialog v-model="showDurationUpdateDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline">Update Durations?</v-card-title>
        <v-card-text>
          You've changed the default {{ durationUpdateInfo.type }} duration from {{ durationUpdateInfo.oldValue }} to {{ durationUpdateInfo.newValue }} minutes.
          Apply this change to all existing {{ durationUpdateInfo.type }} levels that use the default duration?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="handleDurationUpdate(false)">Default Only</v-btn>
          <v-btn color="primary" text @click="handleDurationUpdate(true)">Update All</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import ChipColorPicker from './ChipColorPicker.vue';
import { defaultDisplayConfig, mergeDisplayConfig, DISPLAY_MODULES, DISPLAY_SIZES, ACCENT_COLORS } from '@/utils/displayConfig';

const props = defineProps({
  structure: { type: Object, default: null },
  isAdmin: { type: Boolean, default: false },
  showAnnouncement: { type: Boolean, default: false }
});

const emit = defineEmits(['change']);

// ── form state ──────────────────────────────────────────────────────────────

const mainDisplay = ref('BASIC');
const durationMinutes = ref(12);
const breakMinutes = ref(5);
const buyinAmount = ref(0);
const rebuyAmount = ref(0);
const addonAmount = ref(0);
const rebuyAllowed = ref(true);
const rebuyEndLevel = ref(null);
const addonAllowed = ref(true);
const buyinChips = ref(1000);
const addonChips = ref(1000);
const maxTables = ref(0);
const maxPlayersPerTable = ref(10);
const trackPlayersByName = ref(false);
const isGlobalStructure = ref(false);
const isTemplateStructure = ref(false);
const announcementText = ref('');
const startCountdownSeconds = ref(0);
const betweenRoundsPauseSeconds = ref(0);
const buyinRake = ref(0);
const rebuyRake = ref(0);
const addonRake = ref(0);

const defaultPayoutTables = () => ([
  { minEntries: 1,  maxEntries: 8,   numberOfPayouts: 3, prizesTemplate: [50, 30, 20],       rounding: 'roundToNearestDollar', usePercent: true },
  { minEntries: 9,  maxEntries: 16,  numberOfPayouts: 4, prizesTemplate: [40, 25, 20, 15],   rounding: 'roundToNearestDollar', usePercent: true },
  { minEntries: 17, maxEntries: 999, numberOfPayouts: 5, prizesTemplate: [35, 22, 18, 15, 10], rounding: 'roundToNearestDollar', usePercent: true }
]);

const payoutTables = ref(defaultPayoutTables());
const roundingOptions = ref([
  { label: '$1',  value: 'roundToNearestDollar' },
  { label: '$5',  value: 'roundToNearestFiveDollars' },
  { label: '$10', value: 'roundToNearestTenDollars' }
]);

const defaultChipDenominations = () => ([
  { value: 25,   color: '#008000', accentColor: '#FFFFFF' },
  { value: 100,  color: '#212121', accentColor: '#FFFFFF' },
  { value: 500,  color: '#6a1b9a', accentColor: '#FFFFFF' },
  { value: 1000, color: '#fdd835', accentColor: '#000000' },
  { value: 5000, color: '#ff6f00', accentColor: '#FFFFFF' },
]);

const chipDenominations = ref(defaultChipDenominations());
const selectedChipIndex = ref(null);

const defaultLevel = () => ({
  smallBlind: 100,
  bigBlind: 200,
  bigBlindAnte: 0,
  useBBAnte: false,
  isBreak: false,
  duration: null
});

const levels = ref([defaultLevel()]);
const displayConfig = ref(defaultDisplayConfig());

// dialog state
const addLevelDialog = ref(false);
const newLevelType = ref('normal');
const addLevelIndex = ref(-1);
const addLevelBtnRef = ref(null);
const smallBlindRefs = {};
const showDurationUpdateDialog = ref(false);
const durationUpdateInfo = ref({ type: '', oldValue: 0, newValue: 0, oldLevelValue: 12, oldBreakValue: 5 });
const showMaxPlayersDialog = ref(false);
let previousMaxPlayers = maxPlayersPerTable.value;

// ── load from prop ───────────────────────────────────────────────────────────

let suppressChange = false;

function loadFromStructure(structure) {
  if (!structure) return;
  suppressChange = true;
  durationMinutes.value = structure.defaultDuration ?? 12;
  breakMinutes.value = structure.breakDuration ?? 5;
  durationUpdateInfo.value.oldLevelValue = structure.defaultDuration ?? 12;
  durationUpdateInfo.value.oldBreakValue = structure.breakDuration ?? 5;
  buyinAmount.value = structure.buyinAmount ?? 0;
  rebuyAmount.value = structure.rebuyAmount ?? 0;
  addonAmount.value = structure.addonAmount ?? 0;
  rebuyAllowed.value = structure.rebuyAllowed ?? !!structure.rebuyAmount;
  rebuyEndLevel.value = structure.rebuyEndLevel ?? null;
  addonAllowed.value = structure.addonAllowed ?? !!structure.addonAmount;
  buyinChips.value = structure.buyinChips ?? 1000;
  addonChips.value = structure.addonChips ?? 1000;
  maxTables.value = structure.maxTables ?? 0;
  maxPlayersPerTable.value = structure.maxPlayersPerTable ?? 10;
  trackPlayersByName.value = structure.trackPlayersByName ?? false;
  isGlobalStructure.value = structure.isGlobal ?? false;
  isTemplateStructure.value = structure.isTemplate ?? false;
  announcementText.value = structure.announcement ?? '';
  startCountdownSeconds.value = structure.startCountdownSeconds ?? 0;
  betweenRoundsPauseSeconds.value = structure.betweenRoundsPauseSeconds ?? 0;
  buyinRake.value = structure.buyinRake ?? 0;
  rebuyRake.value = structure.rebuyRake ?? 0;
  addonRake.value = structure.addonRake ?? 0;
  if (Array.isArray(structure.levels) && structure.levels.length > 0) {
    levels.value = structure.levels.map(l => ({ ...l }));
  } else {
    levels.value = [defaultLevel()];
  }
  if (Array.isArray(structure.chipDenominations) && structure.chipDenominations.length > 0) {
    chipDenominations.value = structure.chipDenominations.map(c => ({ ...c }));
  } else {
    chipDenominations.value = defaultChipDenominations();
  }
  selectedChipIndex.value = null;
  if (Array.isArray(structure.payoutTables) && structure.payoutTables.length > 0) {
    payoutTables.value = structure.payoutTables.map(t => ({
      ...t,
      prizesTemplate: [...(t.prizesTemplate || [])],
      usePercent: t.usePercent !== undefined ? t.usePercent : true
    }));
  } else {
    payoutTables.value = defaultPayoutTables();
  }
  displayConfig.value = mergeDisplayConfig(structure.displayConfig);
  nextTick(() => { suppressChange = false; });
}

watch(() => props.structure, (val) => {
  loadFromStructure(val);
}, { immediate: true, deep: true });

// ── build output ─────────────────────────────────────────────────────────────

function buildStructureData() {
  return {
    defaultDuration: durationMinutes.value,
    breakDuration: breakMinutes.value,
    levels: levels.value,
    buyinAmount: buyinAmount.value,
    rebuyAmount: rebuyAmount.value,
    addonAmount: addonAmount.value,
    rebuyAllowed: rebuyAllowed.value,
    rebuyEndLevel: rebuyEndLevel.value,
    addonAllowed: addonAllowed.value,
    buyinChips: buyinChips.value,
    addonChips: addonChips.value,
    maxTables: maxTables.value,
    maxPlayersPerTable: maxPlayersPerTable.value,
    trackPlayersByName: trackPlayersByName.value,
    chipDenominations: chipDenominations.value,
    payoutTables: payoutTables.value,
    isGlobal: isGlobalStructure.value,
    isTemplate: isTemplateStructure.value,
    announcement: announcementText.value,
    startCountdownSeconds: startCountdownSeconds.value,
    betweenRoundsPauseSeconds: betweenRoundsPauseSeconds.value,
    buyinRake: buyinRake.value,
    rebuyRake: rebuyRake.value,
    addonRake: addonRake.value,
    displayConfig: displayConfig.value,
  };
}

// ── watchers → emit change ───────────────────────────────────────────────────

const watchTargets = [
  durationMinutes, breakMinutes, buyinAmount, rebuyAmount, addonAmount,
  rebuyAllowed, rebuyEndLevel, addonAllowed, buyinChips, addonChips,
  maxTables, maxPlayersPerTable, trackPlayersByName,
  isGlobalStructure, isTemplateStructure, announcementText, levels, chipDenominations, payoutTables,
  startCountdownSeconds, betweenRoundsPauseSeconds,
  buyinRake, rebuyRake, addonRake, displayConfig
];

watch(watchTargets, () => {
  if (!suppressChange) emit('change', buildStructureData());
}, { deep: true });

// ── max players confirmation ─────────────────────────────────────────────────

watch(maxPlayersPerTable, (newValue, oldValue) => {
  if (newValue > 10) {
    previousMaxPlayers = oldValue;
    showMaxPlayersDialog.value = true;
  }
});

function confirmMaxPlayersChange() { showMaxPlayersDialog.value = false; }
function cancelMaxPlayersChange() {
  maxPlayersPerTable.value = previousMaxPlayers;
  showMaxPlayersDialog.value = false;
}

// ── chips ────────────────────────────────────────────────────────────────────

function addChip() {
  chipDenominations.value.push({ value: null, color: '#FFFFFF', accentColor: '#000000' });
  selectedChipIndex.value = chipDenominations.value.length - 1;
}

function removeSelectedChip() {
  if (selectedChipIndex.value === null) return;
  chipDenominations.value.splice(selectedChipIndex.value, 1);
  selectedChipIndex.value = chipDenominations.value.length > 0
    ? Math.min(selectedChipIndex.value, chipDenominations.value.length - 1)
    : null;
}

function getContrastingTextColor(hexcolor) {
  if (!hexcolor) return '#000000';
  if (hexcolor.slice(0, 1) === '#') hexcolor = hexcolor.slice(1);
  if (hexcolor.length === 3) hexcolor = hexcolor.split('').map(h => h + h).join('');
  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);
  return (((r * 299) + (g * 587) + (b * 114)) / 1000 >= 128) ? '#000000' : '#FFFFFF';
}

// ── levels ───────────────────────────────────────────────────────────────────

function onSmallBlindChange(idx) {
  const row = levels.value[idx];
  if (row.isBreak) return;
  row.bigBlind = 2 * row.smallBlind;
  if (row.useBBAnte) row.bigBlindAnte = row.bigBlind;
}

function onBigBlindBlur(idx) {
  const row = levels.value[idx];
  if (row.isBreak || !row.useBBAnte) return;
  row.bigBlindAnte = row.bigBlind;
}

function onBBAnteChange(idx) {
  const row = levels.value[idx];
  if (row.isBreak) return;
  row.bigBlindAnte = row.useBBAnte ? row.bigBlind : 0;
}

function setSmallBlindRef(el, idx) {
  if (el) smallBlindRefs[idx] = el;
  else delete smallBlindRefs[idx];
}

function focusAddLevelBtn() {
  addLevelBtnRef.value?.$el?.focus();
}

function showAddLevelDialog(idx) {
  addLevelIndex.value = idx;
  newLevelType.value = 'normal';
  addLevelDialog.value = true;
}

function confirmAddLevel() {
  const idx = addLevelIndex.value;
  const newIdx = idx + 1;
  const isBreak = newLevelType.value === 'break';
  if (isBreak) {
    levels.value.splice(newIdx, 0, { smallBlind: 0, bigBlind: 0, bigBlindAnte: 0, useBBAnte: false, isBreak: true, duration: null, breakMessage: '' });
  } else {
    let referenceLevel = null;
    for (let i = idx; i >= 0; i--) {
      if (!levels.value[i].isBreak) { referenceLevel = levels.value[i]; break; }
    }
    if (!referenceLevel) referenceLevel = { smallBlind: 100, bigBlind: 200, bigBlindAnte: 0, useBBAnte: false };
    const newSmallBlind = referenceLevel.smallBlind * 2;
    const newBigBlind = newSmallBlind * 2;
    const newUseBBAnte = referenceLevel.useBBAnte;
    const newBigBlindAnte = newUseBBAnte ? newBigBlind : referenceLevel.bigBlindAnte * 2;
    levels.value.splice(newIdx, 0, { smallBlind: newSmallBlind, bigBlind: newBigBlind, bigBlindAnte: newBigBlindAnte, useBBAnte: newUseBBAnte, isBreak: false, duration: null });
  }
  addLevelDialog.value = false;
  if (!isBreak) {
    nextTick(() => { smallBlindRefs[newIdx]?.focus(); });
  }
}

function deleteRow(idx) {
  if (levels.value.length > 1) levels.value.splice(idx, 1);
}

function getNormalLevelNumber(index) {
  let count = 0;
  for (let i = 0; i <= index; i++) {
    if (!levels.value[i].isBreak) count++;
  }
  return count;
}

// ── duration update dialog ───────────────────────────────────────────────────

function onDurationBlur(type) {
  let oldValue, newValue;
  if (type === 'level') {
    oldValue = durationUpdateInfo.value.oldLevelValue;
    newValue = durationMinutes.value;
  } else {
    oldValue = durationUpdateInfo.value.oldBreakValue;
    newValue = breakMinutes.value;
  }
  if (newValue !== oldValue && newValue !== null && newValue !== undefined) {
    durationUpdateInfo.value = { ...durationUpdateInfo.value, type, oldValue, newValue };
    showDurationUpdateDialog.value = true;
  }
}

function handleDurationUpdate(updateAll) {
  if (updateAll) {
    const { type, newValue } = durationUpdateInfo.value;
    const isBreak = type === 'break';
    levels.value.forEach((level, index) => {
      if (level.isBreak === isBreak) levels.value[index].duration = newValue;
    });
  }
  if (durationUpdateInfo.value.type === 'level') {
    durationUpdateInfo.value.oldLevelValue = durationUpdateInfo.value.newValue;
  } else {
    durationUpdateInfo.value.oldBreakValue = durationUpdateInfo.value.newValue;
  }
  showDurationUpdateDialog.value = false;
}

// ── resets when allowed flags toggled ────────────────────────────────────────

watch(rebuyAllowed, (val) => { if (!val) rebuyAmount.value = 0; });
watch(addonAllowed, (val) => {
  if (!val) { addonAmount.value = 0; addonChips.value = 0; }
});
watch(trackPlayersByName, (val) => {
  if (val && !maxTables.value) maxTables.value = 1;
});

// ── payouts ──────────────────────────────────────────────────────────────────

function addPrize(tableIndex) { payoutTables.value[tableIndex].prizesTemplate.push(0); }
function removePrize(tableIndex, idx) {
  if (payoutTables.value[tableIndex].prizesTemplate.length > 1) {
    payoutTables.value[tableIndex].prizesTemplate.splice(idx, 1);
  }
}
function onPrizePercentChange(tableIndex, idx) {
  const t = payoutTables.value[tableIndex];
  let val = t.prizesTemplate[idx];
  if (val < 0) t.prizesTemplate[idx] = 0;
  if (t.usePercent && val > 100) t.prizesTemplate[idx] = 100;
}
function updatePrizesTemplate(tableIndex) {
  const t = payoutTables.value[tableIndex];
  if (t.numberOfPayouts < t.prizesTemplate.length) {
    t.prizesTemplate = t.prizesTemplate.slice(0, t.numberOfPayouts);
  }
  while (t.prizesTemplate.length < t.numberOfPayouts) {
    t.prizesTemplate.push(0);
  }
}
function addPayoutTable() {
  payoutTables.value.push({ minEntries: 1, maxEntries: 16, numberOfPayouts: 3, prizesTemplate: [50, 30, 20], rounding: 'roundToNearestDollar', usePercent: true });
}
function removePayoutTable(tableIndex) {
  if (payoutTables.value.length > 1) payoutTables.value.splice(tableIndex, 1);
}
function validateEntryRanges() {
  for (const table of payoutTables.value) {
    if (table.minEntries > table.maxEntries) table.minEntries = table.maxEntries;
  }
}
function hasEntryRangeConflict(currentTableIndex) {
  for (let i = 0; i < payoutTables.value.length; i++) {
    if (i === currentTableIndex) continue;
    const cur = payoutTables.value[currentTableIndex];
    const oth = payoutTables.value[i];
    if (cur.minEntries >= oth.minEntries && cur.minEntries <= oth.maxEntries) return true;
    if (cur.maxEntries >= oth.minEntries && cur.maxEntries <= oth.maxEntries) return true;
    if (oth.minEntries >= cur.minEntries && oth.minEntries <= cur.maxEntries) return true;
    if (oth.maxEntries >= cur.minEntries && oth.maxEntries <= cur.maxEntries) return true;
  }
  return false;
}
function getPrizesTotal(tableIndex) {
  return payoutTables.value[tableIndex].prizesTemplate.reduce((a, b) => Number(a) + Number(b), 0);
}
function getDollarPayoutWarning(tableIndex) {
  const t = payoutTables.value[tableIndex];
  if (t.usePercent || !buyinAmount.value) return null;
  const total = getPrizesTotal(tableIndex);
  const maxPool = buyinAmount.value * t.maxEntries;
  if (total > maxPool) {
    return `Total payout ($${total}) exceeds the maximum prize pool ($${maxPool} = $${buyinAmount.value} buyin × ${t.maxEntries} max players)`;
  }
  return null;
}
</script>

<style>
.accent-swatch {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #999;
  cursor: pointer;
  outline-offset: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}
.chip-visual {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 1.2rem;
  border: 8px dashed;
  box-shadow: 0 4px 8px rgba(0,0,0,0.2), inset 0 2px 4px rgba(255,255,255,0.5);
  text-shadow: 0 1px 1px rgba(0,0,0,0.2);
}
</style>
